// © 2026 Orpyx Medical Technologies Inc.

import * as React from "react";
import {memo, useEffect} from "react";
import {Box, Button, TextField as MuiTextField} from "@material-ui/core";
import FormLabel from "@material-ui/core/FormLabel";
import {Field, withFormik, useFormikContext} from "formik";
import {CheckboxWithLabel, TextField} from "formik-material-ui";
import {
    isNullOrEmpty,
    ModalWindowSubmitButton,
    OperationProgress,
    PropertyNamePipe,
    useModalWindowStyle
} from "orpyx-web-common";
import {PatientsCreateFormMeta, PatientSubmitCareNoteFormProps, PatientSubmitCareNoteFormValues} from "./patient-submit-care-note-form.interface";
import {PatientSubmitCareNoteForValidator} from "./patient-submit-care-note-form.validators";
import {onSubmitPatientSubmitCareNoteForm} from "./patient-submit-care-note-form.hooks";
import {CarePractitionerActionSelector} from "../care-practitioner-action-selector/care-practitioner-action-selector";
import {PrimaryConcernSelector} from "../primary-concern-selector/primary-concern-selector";
import {primaryConcernsForAction} from "../primary-concern-selector/primary-concerns";
import {SuggestedActionSelector} from "../suggested-action-selector/suggested-action-selector";
import {suggestedActionsForConcern} from "../suggested-action-selector/suggested-actions";
import {IPrimaryConcernDto, ISuggestedActionDto} from "doctors-dashboard/http-clients/index";
import InputAdornment from "@material-ui/core/InputAdornment";
import {TimeSpan} from "../../../../../../shared/utility/timeSpan";
import { usePatientSubmitCareNoteFormStyles } from "./patient-submit-care-note-form.style";

const additionalMinutesInputAdornments = {
    endAdornment: (<InputAdornment position="start">override time</InputAdornment>)
};

const elapsedMinutesInputAdornments = {
    endAdornment: (<InputAdornment position="start">elapsed time</InputAdornment>)
};

const labelOverrideTime = {
    label: 'Override time',
};

const PatientSubmitCareNoteFormTemplate = (props: PatientsCreateFormMeta) => {
    const classes = usePatientSubmitCareNoteFormStyles();
    const modalClasses = useModalWindowStyle();
    const { resetForm } = useFormikContext();
    // Action #9 is "Clinical Review - Stable (No Patient Contact)", which offers no primary concern
    const withinLimitValues = { 
        comment: 'Monitoring data acknowledged and within expected parameters. No clinical action required at this time.', 
        actionId: 9,
        primaryConcernId: '',
        suggestedActionId: '',
        overrideTime: false 
    };

    const primaryConcerns: IPrimaryConcernDto[] = primaryConcernsForAction(props.actions, props.values.actionId);
    const suggestedActions: ISuggestedActionDto[] = suggestedActionsForConcern(primaryConcerns, props.values.primaryConcernId);

    useEffect(() => {
        props.setSubmitting(props.inProcess)
    }, [props.inProcess]);

    // A concern belongs to the action it was picked under, so switching action has to drop it.
    // Without this the form can submit a pair the API rejects. The suggested action goes with it -
    // it belongs to that concern, and the API ignores it without one.
    useEffect(() => {
        if (props.values.primaryConcernId) {
            props.setFieldValue('primaryConcernId', '');
        }

        if (props.values.suggestedActionId) {
            props.setFieldValue('suggestedActionId', '');
        }
    }, [props.values.actionId]);

    // Likewise a suggested action belongs to the concern it was picked under, so switching concern
    // drops it rather than carrying it over as a default. The exception is a concern with a single
    // suggested action: that one is the only valid answer, so it is filled in for the nurse.
    useEffect(() => {
        const forcedSuggestedActionId: string = suggestedActions.length === 1 ? String(suggestedActions[0].id) : '';

        if (String(props.values.suggestedActionId) !== forcedSuggestedActionId) {
            props.setFieldValue('suggestedActionId', forcedSuggestedActionId);
        }
    }, [props.values.primaryConcernId]);

    return (
        <div>
            {props.hasAlerts !== null && props.hasAlerts === false && <div className={classes.withinLimitsDiv}>
                <Button
                    id = "within_limits_button"
                    variant = "contained"
                    color = "primary"
                    disabled={props.isSubmitting}
                    className={classes.withinLimitsButton}
                    onClick={() => resetForm({values: withinLimitValues})}
                    >
                    Within Limits
                </Button>
            </div>}
            <h2 className={classes.title}>
                Comments / Notes
            </h2>
            <form onSubmit={props.handleSubmit} onKeyUp={props.onActiveInput}>
                <Box mb={3} pr={3}>
                    <FormLabel>
                        Comments / Assessment
                    </FormLabel>
                    <Field
                        component={TextField}
                        className={modalClasses.input}
                        placeholder={'Enter comments / notes'}
                        name={PropertyNamePipe(props.values, 'comment')}
                        multiline={true}
                        rows={10}
                        type="text"
                    />
                </Box>

                <Box mb={3}>
                    <FormLabel required={true}>
                        Action
                    </FormLabel>

                    <CarePractitionerActionSelector
                        classes={modalClasses}
                        actions={props.actions}
                        selectedAction={props.values.actionId}
                        values={props.values}
                    />
                </Box>

                {primaryConcerns.length > 0 && <Box mb={3}>
                    <FormLabel required={true}>
                        Primary Concern
                    </FormLabel>

                    <PrimaryConcernSelector
                        classes={modalClasses}
                        concerns={primaryConcerns}
                        selectedConcern={props.values.primaryConcernId}
                        values={props.values}
                    />
                </Box>}

                {suggestedActions.length > 0 && <Box mb={3}>
                    <FormLabel required={true}>
                        Suggested Action
                    </FormLabel>

                    <SuggestedActionSelector
                        classes={modalClasses}
                        suggestedActions={suggestedActions}
                        selectedSuggestedAction={props.values.suggestedActionId}
                        values={props.values}
                    />
                </Box>}

                <Box mb={3} pr={3}>
                    <FormLabel required={true}>
                        Time Spent
                    </FormLabel>
                    {props.values.overrideTime ? <Field
                        component={TextField}
                        className={modalClasses.input}
                        placeholder={'Override time'}
                        name={PropertyNamePipe(props.values, 'timeSpent')}
                        InputProps={isNullOrEmpty(props.values.timeSpent) ? null : additionalMinutesInputAdornments}
                        type="text"
                    /> : <MuiTextField
                        className={modalClasses.input}
                        value={TimeSpan.toHoursAndMinutesAndSeconds(props.trackedTime)}
                        InputProps={elapsedMinutesInputAdornments}
                        disabled={true}
                        type="text"
                    />}
                </Box>

                <Box mb={3} pr={3}>
                    <Field
                        component={CheckboxWithLabel}
                        name={PropertyNamePipe(props.values, 'overrideTime')}
                        Label={labelOverrideTime}
                        type='checkbox'
                    />
                </Box>

                <OperationProgress error={props.error} inProcess={props.isSubmitting}/>

                <ModalWindowSubmitButton
                    disabled={props.isSubmitting}
                    content={'Submit'}
                />

            </form>
        </div>
    );
};

export const PatientSubmitCareNoteForm = memo(withFormik<PatientSubmitCareNoteFormProps, PatientSubmitCareNoteFormValues>({
    validate: PatientSubmitCareNoteForValidator,
    handleSubmit: onSubmitPatientSubmitCareNoteForm,
    mapPropsToValues: (props: PatientSubmitCareNoteFormProps): PatientSubmitCareNoteFormValues => ({
        comment: '',
        actionId: '',
        primaryConcernId: '',
        suggestedActionId: '',
        timeSpent: '',
        overrideTime: false,
    }),
})(PatientSubmitCareNoteFormTemplate));
